import React from 'react';
import {
  IconButton,
  SvgPencil,
  Flex,
  SvgX,
  Copy,
  SvgInfo,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';

export interface IChildAccountDisplayProps {
  childAccountName: string;
  uniqueDisplayKey: string;
  isDisabled: boolean;
  changeToEditMode: (uniqueDisplayKey: string) => void;
  removeName: (uniqueDisplayKey: string) => void;
  errorKeys?: string[];
}

export const childAccountErrorsMapping = {
  'firm-name-has-duplicates':
    'There’s already a child account with this name. Please delete the duplicate to continue.',
  'firm-name-already-exists':
    'An account by this name already exists in the system. Please delete this and try again.',
  'firm-name-has-forbidden-characters':
    'Names must only contain uppercase letters, lowercase letters, numbers, spaces, and the following symbols: | _ @ - , ; : . ( ) ‘ & * # \\ + / ! Please edit to continue.',
  'firm-name-max-length-exceeded':
    'Names can’t be more than 50 characters. Please shorten the name to continue.',
};

export function ChildAccountDisplay({
  childAccountName,
  uniqueDisplayKey,
  isDisabled,
  changeToEditMode,
  removeName,
  errorKeys,
}: IChildAccountDisplayProps): JSX.Element {
  function onClickEdit() {
    changeToEditMode(uniqueDisplayKey);
  }
  function onClickDelete() {
    removeName(uniqueDisplayKey);
  }

  const hasError = !!errorKeys?.length;

  const getErrorCopyByKey = (key: string) =>
    childAccountErrorsMapping[key] ?? key;

  return (
    <Flex alignItems="center" justifyContent="space-between">
      <Flex alignItems="center" flex={1}>
        <Stack x="xxx-sm" className="ellipsisOverflow">
          <Copy variant="body" color={hasError ? 'danger' : 'primaryText'}>
            {childAccountName}
          </Copy>
        </Stack>
        {hasError && (
          <Tooltip
            maxWidth="290px"
            roundedContainer
            content={
              <span data-testid="child-account-errors-container">
                {errorKeys.map((errorKey, index) => {
                  const isLastItem = index === errorKeys.length - 1;

                  return (
                    <Stack key={errorKey} y={isLastItem ? null : 'x-sm'}>
                      <Copy variant="body" color="invertedPrimaryText">
                        {getErrorCopyByKey(errorKey)}
                      </Copy>
                    </Stack>
                  );
                })}
              </span>
            }
          >
            <span>
              <Copy variant="body" color="danger">
                <SvgInfo aria-label="tooltip info" size="16px" />
              </Copy>
            </span>
          </Tooltip>
        )}
      </Flex>
      <Flex flexGrow={0}>
        <IconButton
          icon={SvgPencil}
          variant="tertiary"
          buttonSize="small"
          onClick={onClickEdit}
          disabled={isDisabled}
        />
        <IconButton
          icon={SvgX}
          variant="tertiary"
          buttonSize="small"
          onClick={onClickDelete}
          disabled={isDisabled}
        />
      </Flex>
    </Flex>
  );
}
